import React, { useEffect, useState } from "react";
import { ProductNote } from "../../../../shared/services/interface";
import { Button } from "src/business-types/grocery/components/Button";
import InputBox from "../../components/InputBox";
import { FontColor } from "src/shared/modules/profile/theme/themeCheacker";
import { notifyError } from "src/assets/toastService";
import NumberPad from "./NumberPad";
import { ProductRowV2 } from "./NotesProducts";

type Props = {
  selectProductList: ProductRowV2[];
  orderConfirm: (discountedAmount: number, discountValue: number, mode: string) => void;
  receivedAmount: any;
  closeProduct: boolean;
  placeConfirm: () => void;
  clearData: string;
  closeProductTab: () => void;
  paymentMethod: string;
  received: number;
  setResvedAmount: (data: number) => void;
  resvedAmount: number;
  isUnknownCustomer: boolean;
  hasCustomer: boolean;
  deliveryFee?: number;
  parcelFee?: number;
  deliveryFeeEnabled?: boolean;
  parcelFeeEnabled?: boolean;
};

const Footer: React.FC<Props> = ({
  selectProductList,
  orderConfirm,
  receivedAmount,
  closeProduct,
  placeConfirm,
  clearData,
  closeProductTab,
  paymentMethod,
  received,
  isUnknownCustomer,
  hasCustomer,
  setResvedAmount,
  resvedAmount,
  deliveryFee = 0,
  parcelFee = 0,
  deliveryFeeEnabled = false,
  parcelFeeEnabled = false,
}) => {
  const [mode, setMode] = useState<"percent" | "price">("price");
  const [discountValue, setDiscountValue] = useState<string>("");
  const [discountedAmount, setDiscountedAmount] = useState<number>(0);
  const [showPad, setShowPad] = useState(false);

  /** 🔁 Toggle between percent and price discount */
  const handleToggle = () => {
    setMode((prev) => (prev === "percent" ? "price" : "percent"));
    setDiscountValue("");
    setDiscountedAmount(0);
  };

  /** 💰 Calculate total order price */
  const totalPrice = selectProductList.reduce((sum, item) => {
    const qty = Number(item.qty) || 0;
    const price = Number(item.price) || 0;

    const notesTotal = item.notes?.reduce((acc: number, note: ProductNote) => acc + (Number(note.amount) || 0), 0) || 0;

    return sum + qty * price + notesTotal;
  }, 0);

  const applyDiscountValue = (value: string) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      if (mode === "percent" && parseFloat(value) >= 100) return;
      if (mode === "price" && parseFloat(value) >= totalPrice) return;

      setDiscountValue(value);

      const numericValue = parseFloat(value);

      if (!isNaN(numericValue)) {
        const discountAmt = mode === "percent" ? (totalPrice * numericValue) / 100 : numericValue;

        setDiscountedAmount(Math.min(discountAmt, totalPrice));
      } else {
        setDiscountedAmount(0);
      }
    }
  };

  /** 🧮 Compute final amount */
  const baseAmount = Math.max(totalPrice - discountedAmount, 0);
  
  // Add delivery and parcel fees to final amount
  const finalAmount = baseAmount + (deliveryFeeEnabled ? deliveryFee : 0) + (parcelFeeEnabled ? parcelFee : 0);

  /** 🔄 Notify parent about changes */
  useEffect(() => {
    orderConfirm(discountedAmount, Number(discountValue), mode);
  }, [discountedAmount, discountValue, mode]);

  /** 🧹 Reset on clearData change */
  useEffect(() => {
    setDiscountedAmount(0);
    setDiscountValue("");
    setMode("price");
  }, [clearData]);

  const mustPayFull = isUnknownCustomer;

  const btnVisible = (() => {
    if (mustPayFull) {
      return received >= finalAmount; // full payment required
    }

    if (paymentMethod === "Credit") {
      return true; // credit allowed for known customers
    }

    return received > 0;
  })();
  const inputError = mode === "price" && Number(discountValue) > totalPrice;

  const plasedOrder = () => {
    if (!hasCustomer) {
      notifyError("Please select a customer");
      return;
    }

    if (isUnknownCustomer && received < finalAmount) {
      notifyError("Unknown customer must pay full amount");
      return;
    }

    placeConfirm();
  };

  return (
    <div className='w-full border border-gray-100 shadow-inner p-1 rounded-xl bg-gray-100'>
      <div className='w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 justify-center'>
        {/* 🧾 Order Amount */}
        <div className='text-xl border border-gray-200 p-3 rounded-xl shadow-md bg-white min-w-[180px] h-fit flex-1'>
          <p className='text-gray-700'>Order Amount:</p>
          <h1 className='text-4xl md:text-5xl text-red-600 text-right font-bold'>{totalPrice.toFixed(2)}</h1>
        </div>

        {/* 🎁 Discount Section */}
        <div className='text-xl border border-gray-200 p-3 rounded-xl shadow-md bg-white min-w-[240px] h-fit flex-1'>
          <div className='flex flex-col gap-2'>
            <div className='flex justify-between'>
              <p className='text-gray-700'>Discount:</p>
              <div className='flex items-center gap-2'>
                <span className={mode === "percent" ? `${FontColor()} font-semibold` : "text-gray-500"}>%</span>
                <label className='relative inline-flex items-center cursor-pointer'>
                  <input
                    type='checkbox'
                    className='sr-only peer'
                    checked={mode === "price"}
                    onChange={handleToggle}
                    aria-label='Toggle discount mode'
                  />
                  <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-blue-600 transition-all relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                </label>
                <span className={mode === "price" ? `${FontColor()} font-semibold` : "text-gray-500"}>QAR</span>
              </div>
            </div>
            <div className='flex items-center justify-between'>
              <p className='text-green-600'>Value: {discountedAmount.toFixed(2)}</p>

              <div className='w-1/2'>
                <InputBox
                  min={0}
                  type='number'
                  readOnly={true}
                  value={discountValue}
                  disabled={selectProductList.length === 1}
                  error={inputError}
                  onClick={() => {
                    setShowPad(true);
                  }}
                />
              </div>
              {showPad && (
                <NumberPad
                  value={discountValue}
                  onChange={(val) => {
                    const valStr = String(val);
                    applyDiscountValue(valStr);
                  }}
                  onClose={() => {
                    setShowPad(false);
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* ✅ Final Amount - Now includes fees */}
        <div className='text-xl border h-fit border-gray-200 p-3 rounded-xl shadow-md bg-white min-w-[160px] flex-1'>
          <p className='text-gray-700'>Final Amount:</p>
          <h1 className='text-4xl md:text-5xl text-green-600 text-right font-bold'>{finalAmount.toFixed(2)}</h1>
        </div>

        {/* 🛒 Checkout Button */}
        {closeProduct ? (
          <Button
            label='Checkout'
            type='submit'
            variant='confirm'
            className='justify-center h-[100px] flex text-3xl font-bold'
            disabled={selectProductList.length === 0}
            onClick={() => closeProductTab()}
          />
        ) : (
          <Button
            label='Checkout'
            type='submit'
            variant='confirm'
            className='justify-center h-[100px] flex text-3xl font-bold'
            onClick={plasedOrder}
            disabled={!btnVisible}
          />
        )}
      </div>
    </div>
  );
};

export default Footer;